import { __awaiter, __generator } from "tslib";
import { SKIPPED_RESPONSE } from '@amplitude/types';
import { logger, isNodeEnv, isValidEvent } from '@amplitude/utils';
import { RetryHandler } from './retryHandler';
import { SDK_NAME, SDK_VERSION, DEFAULT_OPTIONS } from './constants';

var NodeClient =
/** @class */
function () {
  /**
   * Initializes this client instance.
   *
   * @param apiKey API key for your project
   * @param options options for the client
   */
  function NodeClient(apiKey, options) {
    if (options === void 0) {
      options = {};
    }

    var _a;

    this._events = [];
    this._responseListeners = [];
    this._flushTimer = null;
    this._apiKey = apiKey;
    this._options = Object.assign({}, DEFAULT_OPTIONS, options);

    this._setUpLogging();

    this._transportWithRetry = (_a = this._options.retryClass) !== null && _a !== void 0 ? _a : this._setupDefaultTransport();

    if (!isNodeEnv()) {
      logger.warn('@amplitude/node initialized in a non-node environment and will not work. If you are planning to add Amplitude to a browser environment, please use amplitude-js');
    }
  }
  /**
   * @inheritDoc
   */


  NodeClient.prototype.getOptions = function () {
    return this._options;
  };
  /**
   * @inheritDoc
   */


  NodeClient.prototype.flush = function () {
    return __awaiter(this, void 0, void 0, function () {
      var responseListeners, eventsToSend, response_1, err_1;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            // Clear the timeout
            if (this._flushTimer !== null) {
              clearTimeout(this._flushTimer);
              this._flushTimer = null;
            } // Check if there's 0 events, flush is not needed.


            if (this._events.length === 0) {
              return [2
              /*return*/
              , SKIPPED_RESPONSE];
            }

            responseListeners = this._responseListeners;
            this._responseListeners = [];
            eventsToSend = this._events;
            this._events = [];
            _a.label = 1;

          case 1:
            _a.trys.push([1, 3,, 4]);

            return [4
            /*yield*/
            , this._transportWithRetry.sendEventsWithRetry(eventsToSend)];

          case 2:
            response_1 = _a.sent();
            responseListeners.forEach(function (_a) {
              var resolve = _a.resolve;
              return resolve(response_1);
            });
            return [2
            /*return*/
            , response_1];

          case 3:
            err_1 = _a.sent();
            responseListeners.forEach(function (_a) {
              var reject = _a.reject;
              return reject(err_1);
            });
            throw err_1;

          case 4:
            return [2
            /*return*/
            ];
        }
      });
    });
  };
  /**
   * @inheritDoc
   */


  NodeClient.prototype.logEvent = function (event) {
    return __awaiter(this, void 0, void 0, function () {
      var _this = this;

      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            if (!this._options.optOut) return [3
            /*break*/
            , 2];
            return [4
            /*yield*/
            , Promise.resolve(SKIPPED_RESPONSE)];

          case 1:
            return [2
            /*return*/
            , _a.sent()];

          case 2:
            if (!!isValidEvent(event)) return [3
            /*break*/
            , 4];
            logger.warn('Found invalid event - skipping logEvent action.');
            return [4
            /*yield*/
            , Promise.resolve(SKIPPED_RESPONSE)];

          case 3:
            return [2
            /*return*/
            , _a.sent()];

          case 4:
            this._annotateEvent(event);

            return [4
            /*yield*/
            , new Promise(function (resolve, reject) {
              // Add event to unsent events queue.
              _this._events.push(event);

              _this._responseListeners.push({
                resolve: resolve,
                reject: reject
              });

              if (_this._events.length >= _this._options.maxCachedEvents) {
                // # of events exceeds the limit, flush them.
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                _this.flush();
              } else {
                // Not ready to flush them and not timing yet, then set the timeout
                if (_this._flushTimer === null) {
                  _this._flushTimer = setTimeout(function () {
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    _this.flush();
                  }, _this._options.uploadIntervalInSec * 1000);
                }
              }
            })];

          case 5:
            return [2
            /*return*/
            , _a.sent()];
        }
      });
    });
  };
  /** Add platform dependent field onto event. */


  NodeClient.prototype._annotateEvent = function (event) {
    event.library = SDK_NAME + "/" + SDK_VERSION;
    event.platform = 'Node.js';
  };

  NodeClient.prototype._setupDefaultTransport = function () {
    return new RetryHandler(this._apiKey, this._options);
  };

  NodeClient.prototype._setUpLogging = function () {
    if (this._options.debug || this._options.logLevel > 0) {
      if (this._options.logLevel > 0) {
        logger.enable(this._options.logLevel);
      } else {
        logger.enable();
      }
    }
  };

  return NodeClient;
}();

export { NodeClient };